import { useMemo } from 'react';
import clsx from 'clsx';
import { sanitizeHtml } from '../lib/embeds/sanitizeHtml';
import { parseEmbeds } from '../lib/embeds/parseEmbeds';
import type { EmbedObject, ParsedBody } from '../lib/embeds/types';
import { YouTubeEmbed } from './embeds/YouTubeEmbed';
import { MapEmbed } from './embeds/MapEmbed';
import { ExternalArticleEmbed } from './embeds/ExternalArticleEmbed';
import { DealCard } from './embeds/DealCard';

type Props = {
  html?: string;
  parsed?: ParsedBody;
  className?: string;
};

function renderEmbedComponent(embed: EmbedObject) {
  switch (embed.kind) {
    case 'youtube':
      return <YouTubeEmbed videoId={embed.videoId} />;
    case 'map':
      return <MapEmbed query={embed.query} />;
    case 'external':
      return <ExternalArticleEmbed {...embed} />;
    case 'deal':
      return <DealCard {...embed} />;
    default:
      return null;
  }
}

export function PostBodyRenderer({ html, parsed, className }: Props) {
  const { tokens, embeds } = useMemo(() => {
    if (parsed) return parsed;
    if (html) {
      const sanitized = sanitizeHtml(html);
      return parseEmbeds(sanitized);
    }
    return { tokens: [], embeds: [] };
  }, [html, parsed]);

  const embedMap = useMemo(() => {
    const map = new Map<string, EmbedObject>();
    embeds.forEach((embed) => map.set(embed.id, embed));
    return map;
  }, [embeds]);

  return (
    <div className={clsx(className)}>
      {tokens.map((token, idx) => {
        if (token.type === 'text') {
          return <div key={`text-${idx}`} dangerouslySetInnerHTML={{ __html: token.html }} />;
        }
        const embed = embedMap.get(token.embedId);
        if (!embed) {
          return null;
        }
        return (
          <div key={embed.id} className="embed-wrapper" style={{ margin: '20px 0' }}>
            {renderEmbedComponent(embed)}
          </div>
        );
      })}
    </div>
  );
}
